import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  IconButton,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  TextField,
  InputAdornment,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  Person,
  Phone,
  Email,
  Work,
  Favorite,
  Upload,
  Download,
  Edit,
  Settings,
  TrendingUp,
  Star,
  CheckCircle,
  Description as FileText,
  Shield,
  Notifications as Bell,
  Logout,
  ArrowForward,
  MoreVert,
  Visibility,
  VisibilityOff,
  AccountBalanceWallet,
  CalendarToday,
  LocationOn
} from "@mui/icons-material";
import { apiUrl, fileUrl } from "../config/api";
import useSEO from "../hooks/useSEO";

// Диалог редактирования профиля
const EditProfileDialog = ({ open, onClose, user, editForm, setEditForm, onSave }) => {
  if (!user) return null;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Редактировать профиль</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label="Имя"
            fullWidth
            value={editForm.firstName}
            onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
          />
          <TextField
            label="Фамилия"
            fullWidth
            value={editForm.lastName}
            onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={editForm.email}
            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
            placeholder="example@email.com"
          />
          <TextField
            label="Телефон"
            fullWidth
            value={user.phone}
            disabled
            helperText="Телефон нельзя изменить"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button 
          onClick={onSave} 
          variant="contained"
          disabled={!editForm.firstName.trim() || !editForm.lastName.trim()}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Profile = () => {
  useSEO({ title: 'Профиль | JobMD' });
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [cvFile, setCvFile] = useState(null);
  const [cvPath, setCvPath] = useState("");
  const [stats, setStats] = useState({
    applications: 0,
    interviews: 0,
    offers: 0,
    profileStrength: 65, // Начинаем с 65%, увеличится при загрузке резюме
  });
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: ""
  });
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  useEffect(() => {
    const uid = localStorage.getItem("userId");
    const phone = localStorage.getItem("phone");
    const firstName = localStorage.getItem("firstName");
    const lastName = localStorage.getItem("lastName");
    const cv = localStorage.getItem("cvPath");
    const email = localStorage.getItem("email");

    if (!uid) return;

    setUser({
      id: uid,
      phone: phone || "",
      firstName: firstName || "",
      lastName: lastName || "",
      email: email || ""
    });
    
    // Заполняем форму редактирования
    setEditForm({
      firstName: firstName || "",
      lastName: lastName || "",
      email: email || ""
    });

    setCvPath(cv || "");

    // Обновляем силу профиля в зависимости от наличия резюме
    setStats(prevStats => ({
      ...prevStats,
      profileStrength: cv ? 85 : 65
    }));

    // Загружаем избранное
    loadFavorites();
    
    // Загружаем отклики
    loadApplications();
  }, []);

  // Обновляем избранное при открытии вкладки
  useEffect(() => {
    if (activeTab === "favorites") {
      loadFavorites();
    }
  }, [activeTab]);

  const uploadCv = () => {
    if (!cvFile) {
      alert("Выберите файл");
      return;
    }

    const form = new FormData();
    form.append("userId", user.id);
    form.append("cv", cvFile);

    fetch(apiUrl("upload_cv.php"), {
      method: "POST",
      body: form,
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setCvPath(d.path);
          localStorage.setItem("cvPath", d.path);
        }
      });
  };

  // Функция для удаления вакансии из избранного
  const removeFromFavorites = async (jobId, e) => {
    e.stopPropagation();
    
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    
    try {
      const response = await fetch(apiUrl("favorites.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user: userId,
          job: jobId
        })
      });
      
      const data = await response.json();
      
      if (data.favorite === false) {
        // Успешно удалено - обновляем список
        setFavorites(prevFavorites => prevFavorites.filter(job => job.id !== jobId));
        
        // Обновляем статистику
        setStats(prevStats => ({
          ...prevStats,
          favorites: prevStats.favorites - 1
        }));
      }
    } catch (error) {
      console.error("Ошибка при удалении из избранного:", error);
    }
  };

  // Функция для обновления профиля
  const updateProfile = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(apiUrl("update_profile.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          email: editForm.email
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Обновляем данные пользователя
        setUser(data.user);
        
        // Сохраняем в localStorage
        localStorage.setItem("firstName", data.user.firstName);
        localStorage.setItem("lastName", data.user.lastName);
        if (data.user.email) {
          localStorage.setItem("email", data.user.email);
        }
        
        // Выходим из режима редактирования
        setIsEditing(false);
        
        alert("Профиль успешно обновлен!");
      } else {
        alert("Ошибка обновления профиля: " + data.message);
      }
    } catch (error) {
      console.error("Ошибка при обновлении профиля:", error);
      alert("Ошибка при обновлении профиля");
    }
  };

  // Функция для загрузки откликов
  const loadApplications = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    
    setLoadingApplications(true);
    try {
      const response = await fetch(`${apiUrl("applications.php")}?user=${userId}`);
      const data = await response.json();
      setApplications(data);
      
      // Обновляем статистику
      setStats(prevStats => ({
        ...prevStats,
        applications: data.length || 0
      }));
    } catch (error) {
      console.error("Ошибка загрузки откликов:", error);
      setApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  };

  // Функция для загрузки избранного
  const loadFavorites = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    
    try {
      const response = await fetch(`${apiUrl("favorites.php")}?user=${userId}`);
      const data = await response.json();
      setFavorites(data);
      
      // Обновляем статистику с реальным количеством избранного
      setStats(prevStats => ({
        ...prevStats,
        favorites: data.length || 0
      }));
    } catch (error) {
      console.error("Ошибка загрузки избранного:", error);
      setFavorites([]);
    }
  };


  if (!localStorage.getItem("isLoggedIn")) {
    return (
      <Box textAlign="center" mt={10}>
        <Typography>Вы не авторизованы</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="fade-in" sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, sm: 3 } }}>

      {/* 🔥 HEADER WITH STATS */}
      <Box 
        sx={{
          mb: 4,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(236,72,153,0.08))",
          border: "1px solid rgba(99,102,241,0.1)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack 
          direction={{ xs: "column", md: "row" }} 
          alignItems={{ md: "center" }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Профиль
            </Typography>
            <Typography color="text.secondary">
              Управляй своей карьерой как профессионал 🚀
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Button 
              variant="outlined" 
              startIcon={<Settings />}
              sx={{ borderRadius: 2 }}
              onClick={() => setActiveTab("overview")}
            >
              Настройки
            </Button>
            <Button 
              variant="contained" 
              startIcon={<Edit />}
              sx={{ borderRadius: 2 }}
              onClick={() => setIsEditing(true)}
            >
              Редактировать
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        flexWrap: "wrap",
        gap: "24px",
        boxSizing: "border-box"
      }}>

        {/* 🔥 LEFT SIDEBAR - НА МОБИЛЬНОЙ ПОЛНАЯ ШИРИНА, НА ДЕСКТОПЕ 37% */}
        <Box sx={{
          flex: { xs: "0 0 100%", md: "0 0 37%" },
          width: { xs: "100%", md: "37%" },
          maxWidth: { xs: "100%", md: "37%" },
          boxSizing: "border-box"
        }}>
          <Stack spacing={3} sx={{ alignItems: { xs: "center", md: "stretch" } }}>

            {/* PROFILE CARD */}
            <Card
              sx={{
                borderRadius: 3,
                background: "rgba(255,255,255,0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0,0,0,0.05)",
                boxShadow: "var(--shadow-medium)",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "var(--shadow-floating)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Box
                sx={{
                  height: 80,
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                }}
              />
              
              <CardContent sx={{ pt: 0, position: "relative" }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    border: "4px solid white",
                    position: "absolute",
                    top: -40,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    fontSize: 32,
                    fontWeight: "bold",
                  }}
                >
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </Avatar>
                
                <Box textAlign="center" mt={5}>
                  <Typography variant="h6" fontWeight="bold">
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Соискатель
                  </Typography>
                  
                  <Stack direction="row" justifyContent="center" spacing={1} mt={2}>
                    <Chip 
                      icon={<Phone fontSize="small" />} 
                      label={user.phone}
                      size="small"
                      variant="outlined"
                    />
                    <Chip 
                      icon={<Email fontSize="small" />} 
                      label={user.email || "Email не указан"}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              </CardContent>
              
              <CardActions sx={{ justifyContent: "center", pb: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<Work />}
                  sx={{ borderRadius: 2 }}
                  onClick={() => {
                    setActiveTab("overview");
                    // Можно добавить скролл к разделу откликов или открыть модальное окно
                    alert("Функция 'Мои отклики' в разработке. Отклики: " + applications.length);
                  }}
                >
                  Мои отклики ({applications.length})
                </Button>
              </CardActions>
            </Card>

            {/* PROFILE STRENGTH */}
            <Card
              sx={{
                borderRadius: 3,
                p: 2.5,
                background: "rgba(255,255,255,0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <Typography variant="h6" fontWeight="bold" mb={2}>
                <TrendingUp sx={{ verticalAlign: "middle", mr: 1 }} />
                Полнота профиля
              </Typography>
              
              <Box mb={2}>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.profileStrength} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: "rgba(99,102,241,0.1)",
                    "& .MuiLinearProgress-bar": {
                      background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                      borderRadius: 4,
                    }
                  }} 
                />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {stats.profileStrength}% завершено
                </Typography>
              </Box>
              
              <Stack spacing={1.5}>
                {[
                  { label: "Резюме загружено", completed: !!cvPath, icon: <FileText /> },
                  { label: "Профиль проверен", completed: true, icon: <CheckCircle /> },
                  { label: "Настройки уведомлений", completed: false, icon: <Bell /> },
                  { label: "Безопасность", completed: true, icon: <Shield /> },
                ].map((item, idx) => (
                  <Stack 
                    key={idx} 
                    direction="row" 
                    alignItems="center" 
                    spacing={1.5}
                    sx={{ opacity: item.completed ? 1 : 0.6 }}
                  >
                    <Box sx={{ color: item.completed ? "#10b981" : "#9ca3af" }}>
                      {item.icon}
                    </Box>
                    <Typography variant="body2" flex={1}>
                      {item.label}
                    </Typography>
                    {item.completed ? (
                      <CheckCircle sx={{ fontSize: 16, color: "#10b981" }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Не завершено
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Card>

            {/* QUICK STATS */}
            <Card
              sx={{
                borderRadius: 3,
                p: 2.5,
                background: "rgba(255,255,255,0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <Typography variant="h6" fontWeight="bold" mb={2}>
                <Star sx={{ verticalAlign: "middle", mr: 1 }} />
                Статистика
              </Typography>
              
              <Grid container spacing={2}>
                {[
                  { label: "Откликов", value: stats.applications, icon: <Work />, color: "#6366f1" },
                  { label: "Собеседований", value: stats.interviews, icon: <CalendarToday />, color: "#10b981" },
                  { label: "Предложений", value: stats.offers, icon: <AccountBalanceWallet />, color: "#f59e0b" },
                  { label: "В избранном", value: favorites.length, icon: <Favorite />, color: "#ec4899" },
                ].map((stat, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Box 
                      sx={{ 
                        textAlign: "center",
                        p: 1.5,
                        borderRadius: 2,
                        background: "rgba(0,0,0,0.02)",
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: `${stat.color}15`,
                          color: stat.color,
                          mb: 1,
                        }}
                      >
                        {stat.icon}
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>

          </Stack>
        </Box>

        {/* 🔥 RIGHT SIDEBAR - НА МОБИЛЬНОЙ ПОЛНАЯ ШИРИНА, НА ДЕСКТОПЕ 60% */}
        <Box sx={{
          flex: { xs: "0 0 100%", md: "0 0 60%" },
          width: { xs: "100%", md: "60%" },
          maxWidth: { xs: "100%", md: "60%" },
          boxSizing: "border-box"
        }}>
          <Stack spacing={3}>
            
            {/* TABS - НА МОБИЛЬНОЙ ВЕРТИКАЛЬНЫЕ, НА ДЕСКТОПЕ ГОРИЗОНТАЛЬНЫЕ */}
            <Card
              sx={{
                borderRadius: 3,
                p: 0,
                background: "rgba(255,255,255,0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <Stack 
                direction={{ xs: "column", sm: "row" }}
                sx={{ 
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  px: { xs: 1, sm: 2 },
                }}
              >
                {[
                  { id: "overview", label: "Обзор", icon: <Person /> },
                  { id: "applications", label: "Отклики", icon: <Work /> },
                  { id: "resume", label: "Резюме", icon: <FileText /> },
                  { id: "favorites", label: "Избранное", icon: <Favorite /> },
                  { id: "activity", label: "Активность", icon: <TrendingUp /> },
                ].map((tab) => (
                  <Button
                    key={tab.id}
                    startIcon={tab.icon}
                    onClick={() => setActiveTab(tab.id)}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 3 },
                      borderRadius: 0,
                      borderBottom: activeTab === tab.id ? "2px solid #6366f1" : "none",
                      color: activeTab === tab.id ? "#6366f1" : "inherit",
                      fontWeight: activeTab === tab.id ? 600 : 400,
                      textTransform: "none",
                      transition: "all 0.2s",
                      justifyContent: { xs: "flex-start", sm: "center" },
                      "&:hover": {
                        background: "rgba(99,102,241,0.05)",
                      },
                    }}
                  >
                    {tab.label}
                  </Button>
                ))}
              </Stack>
              
              <Box 
                p={{ xs: 2, sm: 3 }} 
                sx={{ 
                  maxWidth: "100%", 
                  overflow: "hidden",
                  height: { xs: "400px", sm: "500px" }, // ФИКСИРОВАННАЯ высота
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: 6,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(0,0,0,0.1)",
                    borderRadius: 3,
                  },
                  // ГЛОБАЛЬНЫЙ ПЕРЕНОС ТЕКСТА ДЛЯ ВСЕХ ТИПОГРАФИЙ
                  "& .MuiTypography-root": {
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }
                }}
              >
                {activeTab === "overview" && (
                  <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                    <Stack spacing={3} sx={{ maxWidth: "100%", overflow: "hidden" }}>
                      <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ 
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          hyphens: "auto",
                          maxWidth: "100%",
                          display: "block",
                        }}>
                          👋 Добро пожаловать, {user.firstName}!
                        </Typography>
                      </Box>
                      
                      <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                        <Typography 
                          color="text.secondary" 
                          sx={{ 
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto",
                            maxWidth: "100%",
                            display: "block",
                          }}
                        >
                          Ваш профиль почти готов.<br/>Завершите настройку, чтобы увеличить шансы на трудоустройство.
                        </Typography>
                      </Box>
                      
                      <Card
                        sx={{
                          borderRadius: 2,
                          p: 2.5,
                          background: "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(236,72,153,0.08))",
                          border: "1px solid rgba(99,102,241,0.1)",
                          maxWidth: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <Stack 
                          direction={{ xs: "column", sm: "row" }} 
                          alignItems={{ sm: "center" }}
                          justifyContent="space-between"
                          spacing={{ xs: 2, sm: 0 }}
                        >
                          <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                            <Typography fontWeight="bold" sx={{ 
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              hyphens: "auto",
                              maxWidth: "100%",
                              display: "block",
                            }}>
                              🚀 Рекомендации для вас
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              hyphens: "auto",
                              maxWidth: "100%",
                              display: "block",
                            }}>
                              Советы по улучшению профиля
                            </Typography>
                          </Box>
                          <Button 
                            variant="contained" 
                            endIcon={<ArrowForward />}
                            sx={{ borderRadius: 2, flexShrink: 0 }}
                          >
                            Показать
                          </Button>
                        </Stack>
                      </Card>
                    </Stack>
                  </Box>
                )}
                
                {activeTab === "resume" && (
                  <Stack spacing={3}>
                    <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                      📄 Управление резюме
                    </Typography>
                    
                    {cvPath ? (
                      <Card
                        sx={{
                          borderRadius: 2,
                          p: 2.5,
                          background: "rgba(255,255,255,0.9)",
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: 2,
                              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                            }}
                          >
                            <FileText />
                          </Box>
                          <Box flex={1} sx={{ minWidth: 0, overflow: "hidden" }}>
                            <Typography fontWeight="bold" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                              Ваше резюме
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                              Загружено {new Date().toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              startIcon={<Visibility />}
                              href={fileUrl(cvPath)}
                              target="_blank"
                              sx={{ borderRadius: 2 }}
                            >
                              Просмотр
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<Download />}
                              href={fileUrl(cvPath)}
                              download
                              sx={{ borderRadius: 2 }}
                            >
                              Скачать
                            </Button>
                          </Stack>
                        </Stack>
                      </Card>
                    ) : (
                      <Card
                        sx={{
                          borderRadius: 2,
                          p: 3,
                          textAlign: "center",
                          background: "rgba(255,255,255,0.9)",
                          border: "2px dashed rgba(0,0,0,0.1)",
                        }}
                      >
                        <FileText sx={{ fontSize: 48, color: "rgba(0,0,0,0.2)", mb: 2 }} />
                        <Typography fontWeight="bold" mb={1}>
                          Резюме не загружено
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                          Загрузите резюме, чтобы работодатели могли вас найти
                        </Typography>
                      </Card>
                    )}
                    
                    <Card
                      sx={{
                        borderRadius: 2,
                        p: 2.5,
                        background: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <Typography fontWeight="bold" mb={2}>
                        Загрузить новое резюме
                      </Typography>
                      
                      <Stack spacing={2}>
                        <Box>
                          <input
                            type="file"
                            id="cv-upload"
                            onChange={(e) => setCvFile(e.target.files[0])}
                            style={{ display: "none" }}
                          />
                          <label htmlFor="cv-upload">
                            <Button
                              component="span"
                              variant="outlined"
                              startIcon={<Upload />}
                              fullWidth
                              sx={{ borderRadius: 2, py: 1.5 }}
                            >
                              Выбрать файл
                            </Button>
                          </label>
                          {cvFile && (
                            <Typography variant="body2" color="text.secondary" mt={1}>
                              Выбран: {cvFile.name}
                            </Typography>
                          )}
                        </Box>
                        
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={uploadCv}
                          disabled={!cvFile}
                          sx={{ borderRadius: 2, py: 1.5 }}
                        >
                          Загрузить резюме
                        </Button>
                        
                      </Stack>
                    </Card>
                  </Stack>
                )}
                
                {activeTab === "favorites" && (
                  <Stack spacing={3}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                        ⭐ Избранные вакансии
                      </Typography>
                      <Chip 
                        label={`${favorites.length} вакансий`} 
                        variant="outlined" 
                        size="small"
                      />
                    </Stack>
                    
                    {favorites.length === 0 ? (
                      <Card
                        sx={{
                          borderRadius: 2,
                          p: 4,
                          textAlign: "center",
                          background: "rgba(255,255,255,0.9)",
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Favorite sx={{ fontSize: 48, color: "rgba(0,0,0,0.2)", mb: 2 }} />
                        <Typography fontWeight="bold" mb={1} sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                          Нет избранных вакансий
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                          Добавляйте вакансии в избранное, чтобы вернуться к ним позже
                        </Typography>
                      </Card>
                    ) : (
                      <Grid container spacing={2}>
                        {favorites.map((job) => (
                          <Grid item xs={12} key={job.id}>
                            <Card
                              sx={{
                                borderRadius: 2,
                                p: { xs: 1, sm: 1.5 },
                                mb: 1.5,
                                background: "rgba(255,255,255,0.9)",
                                border: "1px solid rgba(0,0,0,0.06)",
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: 1, sm: 2 },
                                transition: "all 0.2s",
                                "&:hover": {
                                  background: "rgba(255,255,255,1)",
                                  borderColor: "rgba(99,102,241,0.2)",
                                  boxShadow: "0 4px 12px rgba(99,102,241,0.1)",
                                  transform: "translateY(-2px)",
                                },
                                cursor: "pointer",
                                maxWidth: "100%",
                                width: "100%",
                              }}
                              onClick={() => window.location.href = `/job/${job.id}`}
                            >
                              {/* Логотип */}
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: 1.5,
                                  background: job.image ? `url(${job.image})` : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                  backgroundSize: "contain",
                                  backgroundRepeat: "no-repeat",
                                  backgroundPosition: "center",
                                  flexShrink: 0,
                                  border: "1px solid rgba(0,0,0,0.05)",
                                }}
                              />
                              
                              {/* Контент */}
                              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight="bold" 
                                  sx={{ 
                                    fontSize: { xs: "0.9rem", sm: "1rem" },
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {job.title}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {job.city} • {job.salary}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {job.dateAdded}
                                </Typography>
                              </Box>
                              
                              {/* Кнопка удаления из избранного */}
                              <IconButton 
                                size="small"
                                sx={{ flexShrink: 0, color: "#ec4899" }}
                                onClick={(e) => removeFromFavorites(job.id, e)}
                                title="Удалить из избранного"
                              >
                                <Favorite fontSize="small" />
                              </IconButton>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Stack>
                )}
                
                {activeTab === "applications" && (
                  <Stack spacing={3}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                        📨 Мои отклики
                      </Typography>
                      <Chip 
                        label={`${applications.length} откликов`} 
                        variant="outlined" 
                        size="small"
                      />
                    </Stack>
                    
                    {loadingApplications ? (
                      <Box textAlign="center" py={4}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" mt={2}>
                          Загрузка откликов...
                        </Typography>
                      </Box>
                    ) : applications.length === 0 ? (
                      <Card
                        sx={{
                          borderRadius: 2,
                          p: 4,
                          textAlign: "center",
                          background: "rgba(255,255,255,0.9)",
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Work sx={{ fontSize: 48, color: "rgba(0,0,0,0.2)", mb: 2 }} />
                        <Typography fontWeight="bold" mb={1} sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                          Нет откликов
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                          Откликайтесь на вакансии, чтобы отслеживать их здесь
                        </Typography>
                      </Card>
                    ) : (
                      <Grid container spacing={2}>
                        {applications.map((app) => (
                          <Grid item xs={12} key={app.application_id}>
                            <Card
                              sx={{
                                borderRadius: 2,
                                p: { xs: 1, sm: 1.5 },
                                mb: 1.5,
                                background: "rgba(255,255,255,0.9)",
                                border: "1px solid rgba(0,0,0,0.06)",
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: 1, sm: 2 },
                                transition: "all 0.2s",
                                "&:hover": {
                                  background: "rgba(255,255,255,1)",
                                  borderColor: "rgba(99,102,241,0.2)",
                                  boxShadow: "0 4px 12px rgba(99,102,241,0.1)",
                                  transform: "translateY(-2px)",
                                },
                                cursor: "pointer",
                                maxWidth: "100%",
                                width: "100%",
                              }}
                              onClick={() => window.location.href = `/job/${app.job_id}`}
                            >
                              {/* Логотип */}
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: 1.5,
                                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                  backgroundSize: "contain",
                                  backgroundRepeat: "no-repeat",
                                  backgroundPosition: "center",
                                  flexShrink: 0,
                                  border: "1px solid rgba(0,0,0,0.05)",
                                }}
                              />
                              
                              {/* Контент */}
                              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight="bold" 
                                  sx={{ 
                                    fontSize: { xs: "0.9rem", sm: "1rem" },
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {app.title}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {app.company} • {app.city} • {app.salary}
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center" mt={0.5}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                      wordBreak: "break-word",
                                      overflowWrap: "break-word",
                                    }}
                                  >
                                    Отправлено: {new Date(app.applied_at).toLocaleDateString()}
                                  </Typography>
                                  <Chip 
                                    label={{
                                      'pending': 'Ожидает',
                                      'viewed': 'Просмотрено',
                                      'rejected': 'Отклонено',
                                      'accepted': 'Принято'
                                    }[app.status] || app.status}
                                    size="small"
                                    color={{
                                      'pending': 'default',
                                      'viewed': 'primary',
                                      'rejected': 'error',
                                      'accepted': 'success'
                                    }[app.status] || 'default'}
                                    variant="outlined"
                                  />
                                </Stack>
                              </Box>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Stack>
                )}
                
                {activeTab === "activity" && (
                  <Stack spacing={3}>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold" 
                      sx={{ 
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        hyphens: "auto",
                        maxWidth: "100%",
                      }}
                    >
                      📈 Активность
                    </Typography>
                    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                      <Typography 
                        color="text.secondary" 
                        sx={{ 
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          hyphens: "auto",
                          maxWidth: "100%",
                          display: "block",
                        }}
                      >
                        Здесь будет отображаться ваша активность:<br/>просмотры, отклики, собеседования.
                      </Typography>
                    </Box>
                    
                    <Card
                      sx={{
                        borderRadius: 2,
                        p: 3,
                        background: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        maxWidth: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                        <TrendingUp sx={{ fontSize: 48, color: "rgba(99,102,241,0.3)", mb: 2 }} />
                        <Typography 
                          fontWeight="bold" 
                          gutterBottom 
                          sx={{ 
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto",
                            maxWidth: "100%",
                          }}
                        >
                          Активность скоро появится
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto",
                            maxWidth: "100%",
                          }}
                        >
                          Следите за своими действиями на платформе
                        </Typography>
                      </Box>
                    </Card>
                  </Stack>
                )}
              </Box>
            </Card>
            
            <Card
              sx={{
                borderRadius: 3,
                p: 2.5,
                background: "rgba(255,255,255,0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0,0,0,0.05)",
                mt: 3,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography fontWeight="bold" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                    Безопасность аккаунта
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                    Последний вход: сегодня в 06:45
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Logout />}
                  onClick={() => {
                    localStorage.clear();
                    window.location.href = "/";
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Выйти
                </Button>
              </Stack>
            </Card>
          </Stack>
        </Box>

      </Box>

      {/* Диалог редактирования профиля */}
      <EditProfileDialog 
        open={isEditing}
        onClose={() => setIsEditing(false)}
        user={user}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={updateProfile}
      />

    </Box>
  );
};

export default Profile;